import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '~/scripts/logger';
interface QueryResult<TData, TError = Error> {
  data: TData | null;
  isLoading: boolean;
  error: TError | null;
  isError: boolean;
}

interface QueryOptions<TData, TError> {
  queryKey: unknown[];
  queryFn: () => Promise<TData>;
  onError?: (error: TError) => void;
}

type ApiError = AxiosError<{
  message: string;
}>;

export function useQuery<TData, TError = ApiError>({
  queryKey,
  queryFn,
  onError,
}: QueryOptions<TData, TError>): QueryResult<TData, TError> {
  const [data, setData] = useState<TData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<TError | null>(null);
  const [isError, setIsError] = useState<boolean>(false);

  // Use refs to store the latest values without causing re-renders
  const queryFnRef = useRef(queryFn);
  const onErrorRef = useRef(onError);

  // Update refs on every render to capture latest values
  queryFnRef.current = queryFn;
  onErrorRef.current = onError;

  // Memoize the fetch function using useCallback with empty deps
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await queryFnRef.current();
      setData(result);
      setError(null);
      setIsError(false);
    } catch (error: unknown) {
      setError(error as TError);
      if (onErrorRef.current) {
        onErrorRef.current(error as TError);
      }
      setIsError(true);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependencies - stable reference

  const queryKeyString = useMemo(() => JSON.stringify(queryKey), [queryKey]);

  useEffect(() => {
    logger.debug(`queryKey`, queryKeyString);
    fetchData();
  }, [queryKeyString, fetchData]); // Now fetchData is stable

  return { data, isLoading, error, isError };
}
